/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-continue */
import {useEffect, useMemo, useState, useCallback} from 'react';
import {
  ShareIcon,
  EyeIcon,
  //   TrashIcon,
  DocumentPlusIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import {useIntl} from 'react-intl';
import {useParams} from 'react-router-dom';
import {toast} from 'react-toastify';

import {CustomersHTTP} from '../../../providers/customers';
import {InvoicesHTTP} from '../../../providers/invoices';
import {
  InvoiceTradeItemsHTTP,
  InvoiceTradesHTTP,
} from '../../../providers/trades';
import {currencyFormat} from '../../../../../utils/currency';

import Button from '../../../../../components/Button';
import Input from '../../../../../components/_form/Input';
import TextArea from '../../../../../components/_form/TextArea';
import Select from '../../../../../components/_form/Select';
import SectionWrapper from '../../../../../components/SectionWrapper';
import PageHeader from '../../../../../components/_layouts/PageHeader';
import Modal from '../../../../../components/_modals/Modal';

import InvoiceCategory from './InvoiceCategory';

const buildTempId = () =>
  `temp_${Date.now()}_${Math.random().toString(16).slice(2)}`;

const NewInvoice = () => {
  const {formatMessage} = useIntl();
  const {id: invoiceId} = useParams();
  const idCripty = window.btoa(invoiceId);

  const [invoice, setInvoice] = useState({
    customerId: '',
    code: '',
    workName: '',
    notes: '',
    invoiceDate: '',
    tax: 0,
    discount: 0,
    disclaimer1Title: '',
    disclaimer1Text: '',
    disclaimer2Title: '',
    disclaimer2Text: '',
  });
  const [customers, setCustomers] = useState({loading: true, data: []});
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletedItemIds, setDeletedItemIds] = useState([]);
  const [deletedTradeIds, setDeletedTradeIds] = useState([]);
  const [openShareModal, setOpenShareModal] = useState(false);

  const fetchCustomers = async () => {
    setCustomers({loading: true, data: []});
    try {
      const {data} = await CustomersHTTP.get({perPage: 150});
      setCustomers({
        loading: false,
        data: data.data.map((c) => ({label: c.name, value: c.id})),
      });
    } catch (err) {
      setCustomers({loading: false, data: []});
    }
  };

  const fetchInvoice = useCallback(
    async (id) => {
      if (!id) return;
      setLoading(true);
      try {
        const {data} = await InvoicesHTTP.getById(id);
        setInvoice({
          code: data.code ?? '',
          customerId: data.customerId,
          workName: data.workName || '',
          notes: data.notes || '',
          invoiceDate: data.invoiceDate ?? '',
          tax: data.tax || 0,
          amountTax: data.amountTax || 0,
          subtotal: data.subtotal || 0,
          discount: data.discount || 0,
          amountDiscountable: data.amountDiscountable || 0,
          disclaimer1Title: data.disclaimer1Title || '',
          disclaimer1Text: data.disclaimer1Text || '',
          disclaimer2Title: data.disclaimer2Title || '',
          disclaimer2Text: data.disclaimer2Text || '',
        });
        setCategories(
          (data.trades || []).map((trade) => ({
            ...trade,
            items: (trade.items || []).map((item) => ({
              ...item,
            })),
          })),
        );
      } catch (err) {
        toast.error(formatMessage({id: 'toast.something-wrong'}));
      } finally {
        setLoading(false);
      }
    },
    [formatMessage],
  );

  useEffect(() => {
    fetchCustomers();
    if (invoiceId) fetchInvoice(invoiceId);
  }, [invoiceId]);

  const handleAddCategory = () => {
    setCategories((prev) => [
      ...prev,
      {
        id: buildTempId(),
        name: '',
        items: [],
        isNew: true,
      },
    ]);
  };

  const handleUpdateCategory = (next) => {
    setCategories((prev) =>
      prev.map((cat) => (cat.id === next.id ? next : cat)),
    );
  };

  const handleRemoveCategory = (category) => {
    if (category.id && !String(category.id).startsWith('temp_')) {
      setDeletedTradeIds((prev) => [...prev, category.id]);
    }
    setCategories((prev) => prev.filter((cat) => cat.id !== category.id));
  };

  const handleAddItem = (category) => {
    const newItem = {
      id: buildTempId(),
      name: '',
      notes: '',
      quantity: 1,
      unitPrice: 0,
      laborIncluded: false,
      laborPrice: 0,
      taxable: false,
      isNew: true,
    };

    handleUpdateCategory({
      ...category,
      items: [...(category.items || []), newItem],
    });
  };

  const handleUpdateItem = (category, item) => {
    handleUpdateCategory({
      ...category,
      items: category.items.map((it) => (it.id === item.id ? item : it)),
    });
  };

  const handleRemoveItem = (category, item) => {
    if (item.id && !String(item.id).startsWith('temp_')) {
      setDeletedItemIds((prev) => [...prev, item.id]);
    }
    handleUpdateCategory({
      ...category,
      items: category.items.filter((it) => it.id !== item.id),
    });
  };

  const calculateTotals = useMemo(() => {
    let amountTaxable = 0;
    const subtotal = categories.reduce((sumCat, cat) => {
      const catTotal = (cat.items || []).reduce((sumItem, item) => {
        const qty = Number(item.quantity) || 0;
        const unit = Number(item.unitPrice) || 0;
        const labor = item.laborIncluded ? Number(item.laborPrice) || 0 : 0;
        const amount = qty * unit + labor;
        const profit = (amount * Number(item.profit)) / 100;

        const itemTotal = amount + profit;
        amountTaxable += item.taxable ? itemTotal : 0;
        return sumItem + itemTotal;
      }, 0);
      return sumCat + catTotal;
    }, 0);

    const amountTax = (amountTaxable * Number(invoice.tax || 0)) / 100;
    const total = subtotal + amountTax;

    const amountDiscount = (total * Number(invoice.discount || 0)) / 100;
    const totalAfterDiscount = total - amountDiscount;

    return {
      subtotal,
      tax: invoice.tax,
      amountTax,
      discount: invoice.discount,
      amountDiscount,
      total,
      totalAfterDiscount,
    };
  }, [categories, invoice.tax, invoice.discount]);

  const handleSave = async () => {
    if (!invoice.workName || !invoice.customerId) {
      toast.error('Please fill invoice title and select customer.');
      return;
    }
    setSaving(true);

    try {
      let savedInvoiceId = invoiceId;
      const invoicePayload = {
        customerId: invoice.customerId,
        workName: invoice.workName,
        notes: invoice.notes,
        invoiceDate: invoice.invoiceDate,
        discount: invoice.discount,
        tax: invoice.tax,
        disclaimer1Title: invoice.disclaimer1Title,
        disclaimer1Text: invoice.disclaimer1Text,
        disclaimer2Title: invoice.disclaimer2Title,
        disclaimer2Text: invoice.disclaimer2Text,
      };

      if (!savedInvoiceId) {
        const {data} = await InvoicesHTTP.create(invoicePayload);
        savedInvoiceId = data?.id;
      } else {
        await InvoicesHTTP.update(savedInvoiceId, invoicePayload);
      }

      if (!savedInvoiceId) {
        throw new Error('Missing invoice id');
      }

      // Create / update trades + items
      const tradeIdMap = {};

      for (const trade of categories) {
        let tradeId = trade.id;
        if (String(tradeId).startsWith('temp_')) {
          const {data} = await InvoiceTradesHTTP.createTrade({
            invoiceId: savedInvoiceId,
            name: trade.name,
          });
          tradeId = data?.id;
        }

        if (!tradeId) continue;
        tradeIdMap[trade.id] = tradeId;

        for (const item of trade.items || []) {
          const payload = {
            invoiceId: savedInvoiceId,
            invoiceTradeId: tradeId,
            name: item.name,
            notes: item.notes,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            price: item.unitPrice * item.quantity,
            laborIncluded: item.laborIncluded,
            laborPrice: item.laborPrice,
            taxable: item.taxable,
            profit: 0,
            profitAmount: 0,
            itemAmount:
              (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0) +
              (item.laborIncluded ? Number(item.laborPrice) || 0 : 0),
          };

          if (String(item.id).startsWith('temp_')) {
            await InvoiceTradeItemsHTTP.createItem(payload);
          } else {
            await InvoiceTradeItemsHTTP.updateItem({id: item.id, payload});
          }
        }
      }

      // Delete removed items and trades
      await Promise.all(
        deletedItemIds.map((itemId) =>
          InvoiceTradeItemsHTTP.deleteItem({id: itemId}),
        ),
      );
      await Promise.all(
        deletedTradeIds.map((tradeId) =>
          InvoiceTradesHTTP.deleteTradeFromInvoice({id: tradeId}),
        ),
      );

      toast.success('Invoice saved successfully');
    } catch (err) {
      toast.error(formatMessage({id: 'toast.something-wrong'}));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader
        title={
          loading
            ? 'loading...'
            : `${formatMessage({id: 'bookpodium.invoice'})} #00${invoice.code}`
        }
        breadcrumbs={[
          {
            id: 'bookpodium',
            label: formatMessage({id: 'menu.bookpodium'}),
          },
          {
            id: 'bookpodium-invoices',
            label: formatMessage({id: 'menu.bookpodium.invoices'}),
          },
        ]}
        rightContent={
          <div className="flex">
            <button
              type="button"
              onClick={() => setOpenShareModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-700 border-r-0 rounded-l-md hover:bg-yellow-500 hover:text-white">
              <ShareIcon className="w-4 h-4" /> Share
            </button>
            <button
              type="button"
              onClick={() =>
                window.open(
                  `https://app.workpodium.com/preview/pdf/${idCripty}`,
                  '_blank',
                )
              }
              className="flex items-center gap-2 px-4 py-2 bg-white border border-l rounded-r-md border-gray-700 hover:bg-yellow-500 hover:text-white">
              <EyeIcon className="w-4 h-4" /> Preview
            </button>
            {/* <button
              type="button"
              onClick={() =>
                dispatchState({
                  estimateConfirmation: {
                    open: true,
                    type: 'delete',
                  },
                })
              }
              className="flex items-center gap-2 px-4 py-2 bg-red-600 border border-gray-700 border-l-0 rounded-r-md text-white hover:bg-red-700">
              <TrashIcon className="w-4 h-4 text-white" /> Delete
            </button> */}
          </div>
        }
      />
      <SectionWrapper
        isLoading={loading}
        showToolbar={false}
        containerClassName="">
        <div className="grid grid-cols-1 gap-4">
          <div className="bg-white rounded-lg border shadow-sm p-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div>
                <Select
                  label={formatMessage({id: 'customer'})}
                  options={customers.data}
                  value={invoice.customerId}
                  onChange={(val) =>
                    setInvoice((prev) => ({...prev, customerId: val}))
                  }
                />
              </div>
              <div>
                <Input
                  label={formatMessage({id: 'work-name-required'})}
                  value={invoice.workName}
                  onChange={(e) =>
                    setInvoice((prev) => ({...prev, workName: e.target.value}))
                  }
                />
              </div>
              <div>
                <Input
                  type="date"
                  label={formatMessage({id: 'invoice-date-required'})}
                  value={invoice.invoiceDate}
                  onChange={(e) =>
                    setInvoice((prev) => ({
                      ...prev,
                      invoiceDate: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Invoice Line Items</h2>
          </div>

          <div className="space-y-1">
            {categories.map((cat) => (
              <InvoiceCategory
                key={cat.id}
                category={cat}
                onUpdate={handleUpdateCategory}
                onRemove={handleRemoveCategory}
                onAddItem={handleAddItem}
                onRemoveItem={handleRemoveItem}
                onUpdateItem={handleUpdateItem}
              />
            ))}
            {categories.length === 0 && (
              <div className="flex flex-col items-center justify-center gap-2 p-4 bg-white rounded-lg border mx-auto">
                <DocumentPlusIcon className="w-8 h-8" />
                <p className="text-sm text-gray-600">
                  Add a category and then add line items inside it.
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-center w-full py">
            <Button type="button" variant="primary" onClick={handleAddCategory}>
              Add new category
            </Button>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4 grid grid-cols-12 gap-2">
            <div className="col-span-2">
              <Input
                label="Tax %"
                type="number"
                value={invoice.tax}
                onChange={(e) =>
                  setInvoice((prev) => ({...prev, tax: e.target.value}))
                }
              />
            </div>
            <div className="col-span-2">
              <Input
                label="Discount %"
                type="number"
                value={invoice.discount}
                onChange={(e) =>
                  setInvoice((prev) => ({...prev, discount: e.target.value}))
                }
              />
            </div>

            <div className="col-span-8">
              <TextArea
                label={formatMessage({id: 'notes'})}
                value={invoice.notes}
                onChange={(e) =>
                  setInvoice((prev) => ({...prev, notes: e.target.value}))
                }
                placeholder="Optional notes"
              />
            </div>

            <div className="col-span-6 p-4 border rounded-lg">
              <Input
                label="Disclaimer"
                value={invoice.disclaimer1Title}
                onChange={(e) =>
                  setInvoice((prev) => ({
                    ...prev,
                    disclaimer1Title: e.target.value,
                  }))
                }
              />
              <TextArea
                label="Disclaimer details"
                value={invoice.disclaimer1Text}
                onChange={(e) =>
                  setInvoice((prev) => ({
                    ...prev,
                    disclaimer1Text: e.target.value,
                  }))
                }
              />
            </div>
            <div className="col-span-6 p-4 border rounded-lg">
              <Input
                label="Additional disclaimer"
                value={invoice.disclaimer2Title}
                onChange={(e) =>
                  setInvoice((prev) => ({
                    ...prev,
                    disclaimer2Title: e.target.value,
                  }))
                }
              />
              <TextArea
                label="Disclaimer details"
                value={invoice.disclaimer2Text}
                onChange={(e) =>
                  setInvoice((prev) => ({
                    ...prev,
                    disclaimer2Text: e.target.value,
                  }))
                }
              />
            </div>
          </div>

          <div
            className="bg-white rounded-lg p-4"
            style={{borderEndEndRadius: 0, borderEndStartRadius: 0}}>
            <div className="flex justify-end gap-4">
              <span className="font-semibold">Subtotal</span>
              <span
                className="font-semibold"
                style={{minWidth: 130, textAlign: 'right'}}>
                {currencyFormat(calculateTotals.subtotal)}
              </span>
            </div>
            <div className="flex justify-end gap-4 mt-2 text-sm text-gray-600">
              <span>Tax ({invoice.tax}%)</span>
              <span style={{minWidth: 130, textAlign: 'right'}}>
                {currencyFormat(calculateTotals.amountTax)}
              </span>
            </div>
            <div className="flex justify-end gap-4 mt-4 text-lg font-bold">
              <span>Total</span>
              <span style={{minWidth: 130, textAlign: 'right'}}>
                {currencyFormat(calculateTotals.total)}
              </span>
            </div>
            {calculateTotals.discount > 0 && (
              <>
                <div className="flex justify-end gap-4 mt-2 text-sm text-red-500">
                  <span>Discount ({invoice.discount}%)</span>
                  <span style={{minWidth: 130, textAlign: 'right'}}>
                    - {currencyFormat(calculateTotals.amountDiscount)}
                  </span>
                </div>
                <div className="flex justify-end gap-4 mt-2  text-lg font-bold">
                  <span>Total after Discount</span>
                  <span style={{minWidth: 130, textAlign: 'right'}}>
                    {currencyFormat(calculateTotals.totalAfterDiscount)}
                  </span>
                </div>
              </>
            )}
          </div>

          <div className="fixed z-10 bottom-0 left-0 right-0 bg-yellow-600 bg-opacity-90 backdrop-blur-md border-t border-yellow-700 py-4 px-6 w-full">
            <div className="flex justify-end">
              <Button
                type="button"
                variant="dark"
                loading={saving}
                onClick={handleSave}>
                Save Invoice
              </Button>
            </div>
          </div>
        </div>
      </SectionWrapper>

      <Modal
        maxWidth="max-w-2xl"
        open={openShareModal}
        onClose={() => setOpenShareModal(false)}
        title="Copy link to share">
        <div className="flex p-4">
          <div className="relative flex items-center justify-between border rounded-md w-full">
            <span className="p-2 text-xs w-11/12 break-words">
              https://app.workpodium.com/preview/pdf/{idCripty}
            </span>
            <button
              type="button"
              onClick={() =>
                navigator.clipboard.writeText(
                  `https://app.workpodium.com/preview/pdf/${idCripty}`,
                )
              }
              className="absolute flex items-center bg-yellow-500 p-2 border-l top-0 right-0 bottom-0">
              <ClipboardDocumentIcon className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default NewInvoice;
